import { EvmChain } from "@axelar-network/axelarjs-sdk";
import { Grid } from "@mui/material";
import { useCallback } from "react";

import { CloseSVG } from "../../assets";
import {
  IOption,
  Modal,
  Options,
  Separator,
  Text,
  Tooltip,
} from "../../components";
import { DISPLAY_CHAIN_TYPE, supportedChainsMap } from "../../config";
import { useCosmJsProvider, useWallet } from "../../contexts";
import {
  useEvmWalletActions,
  useNetworks,
  useSupportedWalletsMap,
} from "../../hooks";
import { ChainData, CosmosChainData, SolanaChainData } from "../../interfaces";
import "./CurrentChainModal.scss";

export const CurrentChainModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: (value: boolean) => void;
}) => {
  const { networks } = useNetworks();
  const walletActions = useEvmWalletActions(networks.evm.provider);
  const { selectedEvmWallet } = useWallet();
  const { connector } = useCosmJsProvider();
  const supportedWalletsMap = useSupportedWalletsMap();

  const isChainSupportedByWallet = useCallback(
    (chainId: string) => {
      const chainName = (
        supportedChainsMap.evm[chainId] ||
        supportedChainsMap.cosmos[chainId] ||
        supportedChainsMap.solana[chainId]
      ).axelarName;
      const supportedByXDEFI = chainName !== EvmChain.MOONBEAM;

      return (
        (selectedEvmWallet === "xdefi" && supportedByXDEFI) ||
        selectedEvmWallet !== "xdefi"
      );
    },
    [selectedEvmWallet],
  );

  const renderChainOption = (
    chain: ChainData | CosmosChainData | SolanaChainData,
  ) => {
    const option = (
      <Grid spacing={1} alignItems="center" container>
        <Grid item>
          <img
            className="current-chain-modal__chain-image"
            src={chain.image}
            alt={chain.displayName}
          />
        </Grid>
        <Grid item>
          <Text
            text={chain.displayName}
            className={
              isChainSupportedByWallet(chain.chainId)
                ? ""
                : "current-chain-modal__chain-not-supported-option"
            }
          />
        </Grid>
      </Grid>
    );

    return isChainSupportedByWallet(chain.chainId) ? (
      option
    ) : (
      <Tooltip
        text={`${
          selectedEvmWallet
            ? supportedWalletsMap.evm[selectedEvmWallet].name
            : ""
        } wallet does not support ${chain.displayName} at the moment`}
        className="current-chain-modal__chain-not-supported-tooltip"
      >
        {option}
      </Tooltip>
    );
  };

  const evmChainOptions: IOption[] = Object.values(supportedChainsMap.evm)
    .filter((chain) => chain.chainType === DISPLAY_CHAIN_TYPE)
    .map((chain) => {
      return {
        id: chain.chainId,
        content: renderChainOption(chain),
      };
    });

  const cosmosChainOptions: IOption[] = Object.values(supportedChainsMap.cosmos)
    .filter((chain) => chain.chainType === DISPLAY_CHAIN_TYPE)
    .map((chain) => {
      return {
        id: chain.chainId,
        content: renderChainOption(chain),
      };
    });

  const solanaChainOptions: IOption[] = Object.values(supportedChainsMap.solana)
    .filter((chain) => chain.chainType === DISPLAY_CHAIN_TYPE)
    .map((chain) => {
      return {
        id: chain.chainId,
        content: renderChainOption(chain),
      };
    });

  return isOpen ? (
    <Modal
      id="current-chain-modal__chain-picker"
      onCloseClick={() => onClose(false)}
      className="current-chain-modal"
    >
      <div className="current-chain-modal__header">
        <div className="current-chain-modal__header-title">Select chain</div>
        <CloseSVG
          className="current-chain-modal__header-x"
          onClick={() => onClose(false)}
        />
      </div>
      <Separator className="current-chain-modal__separator" />

      <p className="current-chain-modal__small-title">EVM</p>
      <Grid className="current-chain-modal__options-grid" container>
        {networks.evm.address ? (
          <Options
            id={networks.evm.chainId}
            handleChange={(chainId) =>
              isChainSupportedByWallet(chainId)
                ? walletActions.switchChain(chainId)
                : () => {}
            }
            options={evmChainOptions}
          ></Options>
        ) : (
          <Text text="Connect EVM wallet first" />
        )}
      </Grid>

      <Separator className="current-chain-modal__separator" />
      <p className="current-chain-modal__small-title">Cosmos</p>
      <Grid className="current-chain-modal__options-grid" container>
        {networks.cosmos.address ? (
          <Options
            id={networks.cosmos.chainId}
            handleChange={(id) => {
              connector.activate(id);
            }}
            options={cosmosChainOptions}
          ></Options>
        ) : (
          <Text text="Connect Cosmos wallet first" />
        )}
      </Grid>

      <Separator className="current-chain-modal__separator" />
      <p className="current-chain-modal__small-title">Solana</p>
      <Grid className="current-chain-modal__options-grid" container>
        {networks.solana.address ? (
          <Options
            id={networks.solana.chainId}
            handleChange={() => {}}
            options={solanaChainOptions}
          ></Options>
        ) : (
          <Text text="Connect Solana wallet first" />
        )}
      </Grid>
    </Modal>
  ) : null;
};
